import { Fragment, useState } from "react";
import { EnvBadge } from "./ui";
import { formatDateTime } from "../lib/time";
import type { AuditLog } from "../lib/types";

const actorLabel: Record<string, string> = {
  platform: "Platform admin",
  developer: "Developer",
  api_key: "Game API key",
  system: "Engine",
};

/** Plain-language summary of an audit action. */
function gameUpdated(m: Record<string, unknown>): string {
  const from = (m.from ?? {}) as { name?: string; slug?: string };
  const to = (m.to ?? {}) as { name?: string; slug?: string };
  const parts: string[] = [];
  if (from.name !== to.name) parts.push(`Renamed game ${from.name ?? ""} to ${to.name ?? ""}`);
  if (from.slug !== to.slug) parts.push(`${parts.length ? "slug" : "Changed game slug"} ${from.slug ?? ""} → ${to.slug ?? ""}`);
  return parts.join(", ") || "Updated game";
}

export function describe(a: AuditLog): string {
  const m = a.metadata ?? {};
  const s = (k: string) => (m[k] === undefined ? "" : String(m[k]));
  const n = (k: string) => (typeof m[k] === "number" ? `${(m[k] as number).toLocaleString()} minor units` : s(k));
  switch (a.action) {
    case "developer.created": return `Registered developer ${s("name")}`;
    case "developer.updated": return "Updated developer profile";
    case "developer.suspended": return "Suspended developer";
    case "developer.reactivated": return "Reactivated developer";
    case "developer_credential.issued": return `Issued developer credential${s("label") ? ` "${s("label")}"` : ""}`;
    case "developer_credential.revoked": return "Revoked a developer credential";
    case "game.created": return `Created game ${s("name")}`;
    case "game.updated": return gameUpdated(m);
    case "game.deleted": return `Deleted game ${s("name")}${typeof m.api_keys_revoked === "number" && m.api_keys_revoked > 0 ? `, revoking ${m.api_keys_revoked} API key${m.api_keys_revoked === 1 ? "" : "s"}` : ""}`;
    case "game.status_changed": return `Game ${s("to") || s("status")}${s("reason") ? `: ${s("reason")}` : ""}`;
    case "api_key.issued": return `Issued ${s("environment")} API key${s("label") ? ` "${s("label")}"` : ""}`;
    case "api_key.revoked": return `Revoked an API key${s("reason") ? `: ${s("reason")}` : ""}`;
    case "bank.created": return `Created bank ${s("name")} (${s("bank_code")}, ${s("base_currency")})`;
    case "bank.updated": return "Updated bank settings";
    case "limits.updated": return "Changed limits";
    case "currency.created": return `Defined currency ${s("code")}`;
    case "clock.configured": return "Changed the game clock";
    case "clock.advanced": return "Advanced the game clock";
    case "player.created": return `Mapped player ${s("external_player_id")}`;
    case "account.opened": return "Opened an account";
    case "account.status_changed": return `Account ${s("to")}${s("reason") ? `: ${s("reason")}` : ""}`;
    case "funding.posted": return `Funded ${n("amount_minor")} (${s("reason")})`;
    case "funding.rejected": return `Rejected funding attempt (${s("error_code")})`;
    case "transfer.posted": return `Transfer of ${n("amount_minor")}`;
    case "transaction.reversed": return `Reversed a ${s("original_type")}: ${s("reason")}`;
    case "hold.placed": return `Placed a hold of ${n("amount_minor")}`;
    case "hold.captured": return `Captured ${n("captured_amount_minor")} from a hold`;
    case "hold.released": return "Released a hold";
    case "hold.expired": return "Hold expired on the game clock";
    case "reconciliation.differences_found": return "Reconciliation found differences";
    default: return a.action;
  }
}

export function AuditTable({ rows, compact, showTenant = true }: { rows: AuditLog[]; compact?: boolean; showTenant?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  if (rows.length === 0) return <p className="muted panel-body">Nothing recorded yet.</p>;
  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>When</th>
            <th>What happened</th>
            {showTenant && <th>Game or developer</th>}
            <th>By</th>
            {!compact && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <Fragment key={a.id}>
              <tr>
                <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(a.created_at)}</td>
                <td>
                  <div>{describe(a)}</div>
                  <div className="cell-sub mono">{a.action}</div>
                </td>
                {showTenant && (
                  <td>
                    <div>{a.game_name ?? a.developer_name ?? "—"}</div>
                    <div className="cell-sub">
                      {a.game_name ? a.developer_name : a.developer_name ? "Developer account" : ""} {a.environment && <EnvBadge env={a.environment} />}
                    </div>
                  </td>
                )}
                <td>{actorLabel[a.actor_type] ?? a.actor_type}</td>
                {!compact && (
                  <td className="num">
                    <button className="btn ghost small" onClick={() => setOpen(open === a.id ? null : a.id)} aria-expanded={open === a.id}>
                      {open === a.id ? "Hide" : "Details"}
                    </button>
                  </td>
                )}
              </tr>
              {open === a.id && (
                <tr>
                  <td colSpan={showTenant ? 5 : 4}>
                    <div className="small muted">
                      Target {a.target_type} <span className="mono">{a.target_id}</span>
                      {a.correlation_id && <>, request <span className="mono">{a.correlation_id}</span></>}
                    </div>
                    <pre className="json">{JSON.stringify(a.metadata, null, 2)}</pre>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
